'use client'

import { useCallback } from 'react'

declare global {
    interface Window {
        snowplow?: (...args: unknown[]) => void
    }
}

function trackEvent(category: string, action: string, label?: string, property?: string) {
    if (typeof window !== 'undefined' && window.snowplow) {
        window.snowplow('trackStructEvent', {
            category,
            action,
            label,
            property,
        })
    }
}

const CATEGORY = 'c2c_search_widget'

/**
 * Hook that provides Snowplow event tracking functions for the search widget.
 * Events are silently dropped if Snowplow is not loaded.
 */
export function useSearchTracking(mode: string) {
    const trackFocus = useCallback(
        (field: 'from' | 'to') => {
            trackEvent(CATEGORY, 'field_focus', mode, field)
        },
        [mode],
    )

    const trackPredictionSelected = useCallback(
        (field: 'from' | 'to', description: string) => {
            trackEvent(CATEGORY, 'prediction_selected', mode, `${field}:${description}`)
        },
        [mode],
    )

    const trackDateSelected = useCallback(
        (date: string) => {
            trackEvent(CATEGORY, 'date_selected', mode, date)
        },
        [mode],
    )

    const trackSubmit = useCallback(
        (from: string, to: string, date: string) => {
            trackEvent(CATEGORY, 'search_submitted', mode, `${from}|${to}|${date}`)
        },
        [mode],
    )

    const trackValidationError = useCallback(
        (fields: string[]) => {
            trackEvent(CATEGORY, 'validation_error', mode, fields.join(','))
        },
        [mode],
    )

    return {
        trackFocus,
        trackPredictionSelected,
        trackDateSelected,
        trackSubmit,
        trackValidationError,
    }
}
